import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  PageLayout,
  Text,
  FormControl,
  Button,
  ActionMenu,
  ActionList,
  ButtonGroup,
} from "@primer/react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  SyncIcon,
  KebabHorizontalIcon,
  CheckIcon,
} from "@primer/octicons-react";
import { Dialog } from "@primer/react/drafts";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { danhSachThiApi } from "../../../api/danhSachThiApi";
import { phongThiApi } from "../../../api/phongThiApi";
import DataTable from "../../../components/ui/data-table";
import { ComboboxTruong } from "../../../components/combobox-truong";
import ComboboxKhoi from "../../../components/combobox-khoi";
import ComboboxKyThi from "../../../components/combobox-kythi";
import ComboboxKyMonThi from "../../../components/combobox-kymonthi";
import ChiaPhongModal from "./chia-phong/ChiaPhongModal";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";

// Interfaces
interface IRoom {
  pl_phonghoc_id: number;
  id: number;
  ten_phong: string;
  ten_loai_phong: string;
  so_thi_sinh: number;
}

interface IStudent {
  id: number;
  ho_ten: string;
  so_bao_danh: string;
  ten_khoi: string;
  ten_phong: string;
  trang_thai: string;
}

interface IFilter {
  dm_truong_id: number;
  dm_khoi_id: number;
  nam_hoc: string;
  ex_kythi_id: number;
  ex_kythi_monthi_id: number;
}

const DanhSachThiPage: React.FC = () => {
  const { nam_hoc } = useCommonSelectedHook();
  const { translate } = useCommonContext();

  // States
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [students, setStudents] = useState<IStudent[]>([]);
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [showRoomAssignModal, setShowRoomAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Filter state
  const [filter, setFilter] = useState<IFilter>({
    dm_truong_id: 0,
    dm_khoi_id: 0,
    nam_hoc: nam_hoc,
    ex_kythi_id: 0,
    ex_kythi_monthi_id: 0,
  });

  // Reset all selections
  const resetSelections = () => {
    setSelectedStudentIds([]);
    setSelectedRoomIds([]);
  };

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load students
      const studentsRes = await danhSachThiApi.selectThiSinhWithRequest(filter);
      if (studentsRes.is_success) {
        setStudents(studentsRes.data);
      }

      // Load rooms if school and exam are selected
      if (filter.dm_truong_id && filter.ex_kythi_monthi_id) {
        const roomsRes = await phongThiApi.selectPhongHoc(
          filter.dm_truong_id,
          filter.ex_kythi_monthi_id
        );
        if (roomsRes.is_success) {
          setRooms(roomsRes.data);
        }
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error loading data"));
    }
    setIsLoading(false);
  }, [filter, translate]);

  // Handle room assignment removal
  const handleRemoveRoomAssignment = async () => {
    setIsSaving(true);
    try {
      const res = await danhSachThiApi.removePhongThiThiSinh({
        ex_kythi_monthi_id: filter.ex_kythi_monthi_id,
        ex_thisinh_ids: selectedStudentIds,
        ex_phongthi_id: 0,
      });

      if (res.is_success) {
        NotifyHelper.Success(
          translate("Successfully removed room assignments")
        );
        setShowDeleteConfirm(false);
        resetSelections();
        await loadData();
      } else {
        NotifyHelper.Error(res.message || translate("Error occurred"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error occurred"));
    }
    setIsSaving(false);
  };

  useEffect(() => {
    loadData();
  }, [filter, loadData]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Filter Section */}
      <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
        <FormControl sx={{ width: "300px" }}>
          <ComboboxTruong
            value={filter.dm_truong_id}
            onValueChanged={(value) => {
              setFilter((prev) => ({ ...prev, dm_truong_id: value || 0 }));
              resetSelections();
            }}
          />
        </FormControl>

        <FormControl sx={{ width: "300px" }}>
          <ComboboxKhoi
            dm_truong_id={filter.dm_truong_id}
            value={filter.dm_khoi_id}
            onValueChanged={(value) => {
              setFilter((prev) => ({ ...prev, dm_khoi_id: value || 0 }));
              resetSelections();
            }}
          />
        </FormControl>

        <FormControl sx={{ width: "400px" }}>
          <ComboboxKyThi
            dm_truong_id={filter.dm_truong_id}
            value={filter.ex_kythi_id}
            onValueChanged={(value) => {
              setFilter((prev) => ({ ...prev, ex_kythi_id: value || 0 }));
              resetSelections();
            }}
          />
        </FormControl>

        <FormControl sx={{ width: "300px" }}>
          <ComboboxKyMonThi
            ex_kythi_id={filter.ex_kythi_id}
            value={filter.ex_kythi_monthi_id}
            onValueChanged={(value) => {
              setFilter((prev) => ({
                ...prev,
                ex_kythi_monthi_id: value || 0,
              }));
              resetSelections();
            }}
          />
        </FormControl>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Left side - Students table */}
        <Box sx={{ flex: "2" }}>
          {/* Student List */}
          <DataTable
            subTitle={`${translate("Base.Label.TotalCount")}: ${
              students.length
            }`}
            height="calc(100vh - 280px)"
            data={students}
            isLoading={isLoading}
            title={translate("Danh sách thí sinh")}
            searchEnable
            paging={{ enable: true, pageSize: 20 }}
            actionComponent={
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="primary"
                  disabled={
                    !selectedStudentIds.length || !selectedRoomIds.length
                  }
                  onClick={() => setShowRoomAssignModal(true)}
                >
                  {translate("Chia phòng")}
                </Button>

                <Button
                  variant="danger"
                  disabled={!selectedStudentIds.length}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  {translate("Bỏ phòng thi của thí sinh")} (
                  {selectedStudentIds.length})
                </Button>
                <Button
                  onClick={() => {
                    resetSelections();
                    loadData();
                  }}
                  leadingVisual={SyncIcon}
                >
                  {translate("Base.Label.Refresh")}
                </Button>

                {/* <MyButton
                  text="Base.Label.Export"
                  leadingVisual={DownloadIcon}
                  onClick={handleExport}
                /> */}
              </Box>
            }
            selection={{
              mode: "multiple",
              onSelectionChanged: setSelectedStudentIds,
              selectedRowKeys: selectedStudentIds,
            }}
            columns={[
              {
                caption: translate("Họ tên"),
                dataField: "ho_ten",
                minWidth: "200px",
              },
              {
                caption: translate("Mã học sinh"),
                dataField: "so_bao_danh",
                width: "120px",
              },
              {
                caption: translate("Khối"),
                dataField: "ten_khoi",
                width: "100px",
              },
              {
                caption: translate("Phòng thi"),
                dataField: "ten_phong",
                width: "120px",
              },
              {
                caption: translate("Trạng thái"),
                dataField: "trang_thai",
                width: "100px",
              },
            ]}
          />
        </Box>

        {/* Right side - Room List */}
        <Box sx={{ flex: "1" }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              height: "100%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                backgroundColor: "canvas.subtle",
              }}
            >
              <Text sx={{ fontWeight: "bold" }}>
                {translate("Danh sách phòng thi")}
              </Text>
            </Box>

            {/* Room List */}
            <Box
              sx={{
                maxHeight: "600px",
                overflow: "auto",
                p: 2,
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Text>{translate("Loading...")}</Text>
                </Box>
              ) : (
                <ActionList selectionVariant="multiple">
                  {rooms.map((room) => (
                    <ActionList.Item
                      key={room.pl_phonghoc_id}
                      selected={selectedRoomIds.includes(room.pl_phonghoc_id)}
                      onSelect={() => {
                        const newSelection = selectedRoomIds.includes(
                          room.pl_phonghoc_id
                        )
                          ? selectedRoomIds.filter(
                              (id) => id !== room.pl_phonghoc_id
                            )
                          : [...selectedRoomIds, room.pl_phonghoc_id];
                        setSelectedRoomIds(newSelection);
                      }}
                    >
                      <ActionList.LeadingVisual>
                        {selectedRoomIds.includes(room.pl_phonghoc_id) ? (
                          <CheckIcon />
                        ) : (
                          <Box sx={{ width: 16, height: 16 }} />
                        )}
                      </ActionList.LeadingVisual>

                      <Box sx={{ flex: 1 }}>
                        <Text sx={{ fontWeight: "bold", display: "block" }}>
                          {room.ten_phong}
                        </Text>
                        <Text
                          sx={{
                            fontSize: 1,
                            color: "fg.muted",
                            display: "block",
                          }}
                        >
                          {room.ten_loai_phong} • {translate("Tổng thí sinh")}:{" "}
                          {room.so_thi_sinh}
                        </Text>
                      </Box>

                      <ActionList.TrailingVisual>
                        {room.so_thi_sinh > 0 && (
                          <Text
                            sx={{
                              fontSize: 0,
                              color: "attention.fg",
                              bg: "attention.subtle",
                              borderRadius: "2em",
                              px: 2,
                              py: 1,
                            }}
                          >
                            {room.so_thi_sinh}
                          </Text>
                        )}
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  ))}
                </ActionList>
              )}
            </Box>

            {/* Footer with count */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid",
                borderColor: "border.default",
                backgroundColor: "canvas.subtle",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text sx={{ fontSize: 1, color: "fg.muted" }}>
                {translate("Đã chọn")}: {selectedRoomIds.length}{" "}
                {translate("phòng thi")}
              </Text>
              {selectedRoomIds.length > 0 && (
                <Button
                  variant="invisible"
                  size="small"
                  onClick={() => setSelectedRoomIds([])}
                >
                  {translate("Clear selection")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      {showRoomAssignModal && (
        <ChiaPhongModal
          is_show_model={showRoomAssignModal}
          onCancel={() => {
            setShowRoomAssignModal(false);
            resetSelections();
          }}
          onSuccess={() => {
            setShowRoomAssignModal(false);
            resetSelections();
            loadData();
          }}
          ex_kythi_monthi_id={filter.ex_kythi_monthi_id}
          tong_thi_sinh={selectedStudentIds.length}
          ex_thisinh_ids={selectedStudentIds}
          pl_phonghoc_ids={selectedRoomIds}
        />
      )}

      {showDeleteConfirm && (
        <Dialog
          title={translate("Lưu ý")}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <Dialog.Body>
            <Text>
              {translate("Bạn có chắc chắn muốn bỏ phòng thi của")}{" "}
              {selectedStudentIds.length} {translate("thí sinh đã chọn")}?
            </Text>
          </Dialog.Body>
          <Dialog.Footer>
            <ButtonGroup>
              <Button onClick={() => setShowDeleteConfirm(false)}>
                {translate("Đóng")}
              </Button>
              <Button
                variant="danger"
                onClick={handleRemoveRoomAssignment}
                disabled={isSaving}
              >
                {translate("Bỏ phòng thi")}
              </Button>
            </ButtonGroup>
          </Dialog.Footer>
        </Dialog>
      )}
    </Box>
  );
};

export default DanhSachThiPage;
